import React, { useState } from 'react';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Badge,
  Checkbox,
  Box,
  Text,
  Divider,
} from '@chakra-ui/react';
import { FiChevronDown, FiFilter } from 'react-icons/fi';

export interface FilterOption<T = string | number> {
  label: string;
  value: T;
}

export interface TableColumnFilterProps<T = string | number> {
  className?: string;
  isMulti?: boolean;
  label: string;
  options: FilterOption<T>[];
  value: T[];
  onChange: (value: T[]) => void;
}

export const TableColumnFilter = <T extends string | number = string | number>({
  label,
  className,
  options,
  value,
  onChange,
  isMulti = false,
}: TableColumnFilterProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOptionClick = (optionValue: T) => {
    if (isMulti) {
      if (value.includes(optionValue)) {
        onChange(value.filter((v) => v !== optionValue) as T[]);
      } else {
        onChange([...value, optionValue] as T[]);
      }
    } else {
      if (value.includes(optionValue)) {
        onChange([] as T[]);
      } else {
        onChange([optionValue] as T[]);
      }
      setIsOpen(false);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  const activeCount = value.length;

  return (
    <Menu isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <MenuButton
        as={Button}
        rightIcon={<FiChevronDown />}
        leftIcon={<FiFilter size={14} />}
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="!font-semibold !text-neutral-700"
        _hover={{ bg: 'gray.100' }}
        _active={{ bg: 'gray.200' }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Text className={className}>{label}</Text>
          {activeCount > 0 && (
            <Badge
              colorScheme="blue"
              borderRadius="full"
              fontSize="xs"
              px={2}
            >
              {activeCount}
            </Badge>
          )}
        </Box>
      </MenuButton>
      <MenuList maxH="300px" overflowY="auto" minW="200px">
        {options.map((option) => {
          const isSelected = value.includes(option.value);
          return (
            <MenuItem
              key={String(option.value)}
              onClick={() => handleOptionClick(option.value)}
              closeOnSelect={false}
              bg={isSelected ? 'blue.50' : undefined}
              _hover={{ bg: isSelected ? 'blue.100' : 'gray.100' }}
            >
              <Checkbox
                isChecked={isSelected}
                mr={2}
                pointerEvents="none"
                colorScheme="blue"
              >
                <span style={{ fontWeight: isSelected ? 600 : 400 }}>
                  {option.label || String(option.value)}
                </span>
              </Checkbox>
            </MenuItem>
          );
        })}
        {activeCount > 0 && (
          <>
            <Divider my={1} />
            <MenuItem
              onClick={handleClear}
              color="red.500"
              fontWeight="semibold"
            >
              Clear Filters
            </MenuItem>
          </>
        )}
      </MenuList>
    </Menu>
  );
};
